import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { staffApi, formatApiError } from '@/lib/api';
import { useStaffAuth } from '@/contexts/StaffAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { JobRolesField } from '@/components/staff/JobRolesField';
import { StaffRolesManager } from '@/components/staff/StaffRolesManager';
import { BulkAssignActivityDialog } from '@/components/staff/BulkAssignActivityDialog';
import { toast } from 'sonner';
import { PlusCircle, Eye, EyeOff, ChevronDown, ChevronUp, ClipboardList } from 'lucide-react';

const EMPTY_FORM = { name: '', email: '', password: '', role: 'COUNTER', positionIds: [] };

const HeaderRow = () => (
  <TableRow className="bg-bone/60">
    <TableHead>Name</TableHead>
    <TableHead>Email</TableHead>
    <TableHead>Access Level</TableHead>
    <TableHead>Job Roles</TableHead>
    <TableHead>Active</TableHead>
    <TableHead>POS Access</TableHead>
    <TableHead>POS PIN</TableHead>
  </TableRow>
);

export default function StaffPage() {
  const { staff: currentStaff } = useStaffAuth();
  const navigate = useNavigate();
  const isAdmin = currentStaff?.role === 'ADMIN';
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  const [pinValue, setPinValue] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [showInactive, setShowInactive] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = () => staffApi.get('/staff').then(({ data }) => setStaff(data.staff));
  const loadRoles = () => staffApi.get('/scheduling/positions').then(({ data }) => setRoles(data.positions));

  useEffect(() => {
    load();
    loadRoles();
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await staffApi.post('/staff', form);
      toast.success('Staff account created');
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (s) => {
    await staffApi.patch(`/staff/${s.id}`, { isActive: !s.isActive });
    load();
  };

  const togglePosAccess = async (s) => {
    await staffApi.patch(`/staff/${s.id}`, { posEnabled: !s.posEnabled });
    load();
  };

  const savePin = async () => {
    if (!/^\d{4}$/.test(pinValue)) return toast.error('PIN must be exactly 4 digits');
    setPinBusy(true);
    try {
      await staffApi.patch(`/staff/${pinTarget.id}/pin`, { pin: pinValue });
      toast.success(`POS PIN updated for ${pinTarget.name}`);
      setRevealed((r) => ({ ...r, [pinTarget.id]: undefined }));
      setPinTarget(null);
      setPinValue('');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPinBusy(false);
    }
  };

  const revealPin = async (s) => {
    try {
      const { data } = await staffApi.get(`/staff/${s.id}/pin`);
      if (!data.canReveal) {
        return toast.error('This PIN was set before secure viewing was enabled — reset it to a new PIN to view it here.');
      }
      setRevealed((r) => ({ ...r, [s.id]: data.pin }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const hidePin = (id) => setRevealed((r) => ({ ...r, [id]: undefined }));

  const active = staff.filter((s) => s.isActive);
  const inactive = staff.filter((s) => !s.isActive);

  const jobRoleChips = (s) => (
    <div className="flex flex-wrap gap-1">
      {(s.jobRoles || []).length === 0 && <span className="text-xs text-muted-ink">—</span>}
      {(s.jobRoles || []).map((r) => (
        <span key={r.id} className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: (r.color || '#E8833A') + '22', color: r.color || '#B45309' }}>{r.name}</span>
      ))}
    </div>
  );

  const renderRow = (s) => (
    <TableRow key={s.id} data-testid="staff-row">
      <TableCell className="font-medium">
        {isAdmin ? (
          <button
            data-testid={`staff-name-link-${s.id}`}
            onClick={() => navigate(`/admin/staff/${s.id}`)}
            className="text-charcoal hover:text-saffron hover:underline transition-colors duration-150"
          >
            {s.name}
          </button>
        ) : (
          s.name
        )}
      </TableCell>
      <TableCell>{s.email}</TableCell>
      <TableCell>{s.role}</TableCell>
      <TableCell data-testid={`staff-jobroles-${s.id}`}>{jobRoleChips(s)}</TableCell>
      <TableCell>
        <Switch data-testid={`staff-active-toggle-${s.id}`} disabled={!isAdmin} checked={s.isActive} onCheckedChange={() => toggleActive(s)} />
      </TableCell>
      <TableCell>
        <Switch data-testid={`staff-pos-toggle-${s.id}`} disabled={!isAdmin} checked={s.posEnabled} onCheckedChange={() => togglePosAccess(s)} />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          {s.hasPin ? (
            <>
              <span data-testid={`staff-pin-value-${s.id}`} className="font-mono text-sm tracking-[0.25em] text-charcoal min-w-[3.5rem]">
                {revealed[s.id] || '••••'}
              </span>
              <button
                data-testid={`staff-pin-reveal-btn-${s.id}`}
                onClick={() => (revealed[s.id] ? hidePin(s.id) : revealPin(s))}
                title={revealed[s.id] ? 'Hide PIN' : 'Show PIN'}
                className="text-muted-ink hover:text-charcoal"
              >
                {revealed[s.id] ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              {isAdmin && (
                <button data-testid={`staff-set-pin-btn-${s.id}`} onClick={() => { setPinTarget(s); setPinValue(''); }} className="text-sm text-coriander hover:underline">
                  Reset
                </button>
              )}
            </>
          ) : isAdmin ? (
            <button data-testid={`staff-set-pin-btn-${s.id}`} onClick={() => { setPinTarget(s); setPinValue(''); }} className="text-sm text-coriander hover:underline">
              Set PIN
            </button>
          ) : (
            <span className="text-sm text-muted-ink">No PIN set</span>
          )}
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-heading text-2xl font-700 text-charcoal">Staff</h1>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(EMPTY_FORM); }}>
          <DialogTrigger asChild>
            {isAdmin ? (
              <Button data-testid="staff-create-btn" className="bg-saffron hover:bg-saffron-hover">
                <PlusCircle size={16} className="mr-2" /> New Staff Account
              </Button>
            ) : <span />}
          </DialogTrigger>
          <DialogContent data-testid="staff-form-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Staff Account</DialogTitle><DialogDescription>Create a login and assign an access level plus any job roles.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" data-testid="staff-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Temporary Password</Label>
                <PasswordInput data-testid="staff-password-input" toggleTestId="staff-password-toggle" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Access Level</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">Admin / Owner</SelectItem>
                    <SelectItem value="MANAGER">Manager</SelectItem>
                    <SelectItem value="COUNTER">Counter Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <JobRolesField allRoles={roles} setAllRoles={setRoles} value={form.positionIds} onChange={(ids) => setForm({ ...form, positionIds: ids })} />
            </div>
            <DialogFooter>
              <Button data-testid="staff-submit-btn" disabled={busy} onClick={submit} className="bg-saffron hover:bg-saffron-hover">
                {busy ? 'Creating…' : 'Create Account'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="team">
        <TabsList>
          <TabsTrigger value="team" data-testid="staff-tab-team">Team</TabsTrigger>
          <TabsTrigger value="roles" data-testid="staff-tab-roles">Roles</TabsTrigger>
        </TabsList>

        <TabsContent value="team" className="mt-4 space-y-4">
          {isAdmin && (
            <div className="flex justify-end">
              <Button data-testid="bulk-assign-open-btn" variant="outline" size="sm" onClick={() => setBulkOpen(true)} className="gap-1.5">
                <ClipboardList size={15} /> Bulk Assign Activity
              </Button>
            </div>
          )}
          <div className="bg-white rounded-xl border border-border/40 overflow-x-auto">
            <Table>
              <TableHeader><HeaderRow /></TableHeader>
              <TableBody>
                {active.map(renderRow)}
                {active.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-ink py-6 text-sm">No active staff.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {inactive.length > 0 && (
            <div className="bg-white rounded-xl border border-border/40 overflow-hidden">
              <button
                type="button"
                data-testid="staff-inactive-toggle"
                onClick={() => setShowInactive((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-bone/50"
              >
                <span className="font-heading font-700 text-charcoal text-sm">Inactive staff ({inactive.length})</span>
                {showInactive ? <ChevronUp size={18} className="text-muted-ink" /> : <ChevronDown size={18} className="text-muted-ink" />}
              </button>
              {showInactive && (
                <div className="overflow-x-auto border-t border-border/40" data-testid="staff-inactive-list">
                  <Table>
                    <TableHeader><HeaderRow /></TableHeader>
                    <TableBody>{inactive.map(renderRow)}</TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          {isAdmin ? (
            <StaffRolesManager allRoles={roles} setAllRoles={setRoles} />
          ) : (
            <p className="text-sm text-muted-ink">Only an Admin can manage job roles.</p>
          )}
        </TabsContent>
      </Tabs>

      <BulkAssignActivityDialog open={bulkOpen} onOpenChange={setBulkOpen} staff={staff} roles={roles} onDone={() => {}} />

      <Dialog open={!!pinTarget} onOpenChange={(v) => !v && setPinTarget(null)}>
        <DialogContent data-testid="staff-pin-dialog">
          <DialogHeader><DialogTitle>Set POS PIN — {pinTarget?.name}</DialogTitle><DialogDescription>Enter a 4-digit PIN this staff member will use at the POS.</DialogDescription></DialogHeader>
          <div className="space-y-1.5">
            <Label>4-Digit PIN</Label>
            <Input
              data-testid="staff-pin-input"
              maxLength={4}
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="1234"
            />
          </div>
          <DialogFooter>
            <Button data-testid="staff-pin-save-btn" disabled={pinBusy} onClick={savePin} className="bg-saffron hover:bg-saffron-hover">
              {pinBusy ? 'Saving…' : 'Save PIN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
